import React, { useMemo } from "react";
import { Hidden, lighten, makeStyles, Theme, useTheme } from "@material-ui/core";
import { Filter, ViAggregatedPartDamage, ViDamageType } from "../models";
import { Chart } from "react-google-charts";
import { Column } from "react-table";
import FooterTable from "./FooterTable";
import { isMatchingDamage, Translator } from "../services";

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    display: "flex",
    flexDirection: "row",
    [theme.breakpoints.down("sm")]: {
      maxHeight: "100%",
      overflow: "scroll",
    },
  },
  chart: {
    paddingLeft: `${theme.goldenRatio(-1)}rem`,
    width: "300px",
    height: "30vh",
    "& svg": {
      overflow: "visible !important",
    },
    "& svg g:last-of-type": {
      pointerEvents: "none",
    },
  },
}));

function DamagesFooter({
  aggregatedPartDamages,
  hoverFilter,
  isFilterMatch,
  setHoverFilter,
  className,
}: {
  aggregatedPartDamages?: ViAggregatedPartDamage[];
  hoverFilter: Filter[];
  isFilterMatch: (filterProps: Filter[]) => boolean | undefined;
  setHoverFilter?: (filters: Filter[]) => void;
  className?: string;
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isFilter = Boolean(hoverFilter?.length);

  const tableData = useMemo(
    () =>
      aggregatedPartDamages?.map((aPD) => ({
        ...aPD,
        partName: aPD.partIdentifiers.genericPartName,
        side: aPD.partIdentifiers.side,
        filters: [aPD],
      })) || [],
    [aggregatedPartDamages]
  );

  const columns: Column[] = [
    {
      accessor: "partName",
      Header: "Part",
      Cell: ({ cell }: { cell: any }) =>
        Translator.translate(`parts.${cell.value.toLowerCase()}`),
    },
    {
      accessor: "side",
      Header: "Side",
      Cell: ({ cell }: any) =>
        cell.value === "NONE"
          ? ""
          : Translator.translate(`sides.${cell.value.toLowerCase()}`),
    },
    {
      accessor: "damageType",
      Header: "Damage Type",
      Cell: ({ cell }: any) =>
        Translator.translate(`damagetypes.${cell.value.toLowerCase()}`),
    },
    {
      accessor: "relativeSize",
      Header: "Relative Size",
      Cell: ({ cell }: any) => `${Math.max(Math.round(cell.value * 100), 1)}%`,
    },
    {
      accessor: "p",
      Header: "Confidence",
      Cell: ({ cell }: any) => `${Math.max(Math.round(cell.value * 100), 1)}%`,
      sortType: (a: any, b: any) => a.values.p - b.values.p,
    },
  ];

  const chartData = Object.entries(
    aggregatedPartDamages
      ?.map((el) => el.damageType)
      .reduce(
        (obj, el: ViDamageType): Record<ViDamageType, number> => ({
          ...obj,
          [el]: (obj[el] || 0) + 1,
        }),
        {} as Record<ViDamageType, number>
      ) || {}
  );

  const slices = chartData.map(([damageType]) => {
    const isMatch = hoverFilter.some((filter) =>
      isMatchingDamage(filter, { damageType: damageType as ViDamageType })
    );
    const isFaded = Boolean(hoverFilter.length) && !isMatch;
    const color = theme.damageTypes[damageType as ViDamageType];
    return {
      ...(color ? { color: isFaded ? lighten(color, 0.4) : color } : {}),
      offset: isMatch ? 0.1 : 0,
      ...(isFilter
        ? {
            textStyle: isMatch
              ? { fontSize: Math.round(theme.typography.fontSize * 0.8) }
              : { fontSize: 0.5 },
          }
        : {}),
    };
  });

  return (
    <div className={[className, classes.wrapper].join(" ")}>
      <FooterTable
        {...{
          data: tableData,
          columns,
          hoverFilter,
          setHoverFilter,
          isFilterMatch,
          initialState: { sortBy: [{ id: "p", desc: true }] },
        }}
      />
      <Hidden mdDown={true}>
        {Boolean(chartData.length) && (
          <Chart
            className={classes.chart}
            style={{
              alignSelf: "center",
              margin: "auto",
            }}
            chartType="PieChart"
            data={[["Operation", "Count"], ...chartData]}
            options={{
              backgroundColor: "none",
              is3D: true,
              slices,
              chartArea: { height: "100%", width: "100%" },
              legend: { position: "right", alignment: "center" },
            }}
          />
        )}
      </Hidden>
    </div>
  );
}

export default React.memo(DamagesFooter);
